import React, { startTransition, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import requireAuth from '../../components/hocs/requireAuth';
import AdminTemplate from '../../layout/AdminTemplate';
import { App, Layout, Typography, Flex, Table, Input, Card } from 'antd';
import { useGetDomainsCountQuery } from './api/domainsCountApiSlice';
import { SearchOutlined } from '@ant-design/icons';
import PrevNextPagination, { usePageSearchParam } from '../../components/PrevNextPagination';

const META_TAGS = {
  title: 'Page Count - Prerender.io',
};
const { Content } = Layout;
const { Title } = Typography;

const PAGE_SIZE = 100;

const mapToApiPayload = (params, page) => {
  return {
    page,
    pageSize: PAGE_SIZE,
    sort: params.sorter?.field,
    sortDirection: params.sorter?.order === 'ascend' ? 'ASC' : 'DESC',
    domain: params.filters?.domain ? params.filters?.domain[0] : undefined,
  };
};

const DomainsCountPage = () => {
  const { message } = App.useApp();
  const initialParams = {
    sorter: {
      field: 'number_of_urls',
      order: 'ascend',
    },
  };
  const [domains, setDomains] = useState([]);
  const [tableParams, setTableParams] = useState(initialParams);
  const [search, setInput] = useState('');
  const [page, setPage] = usePageSearchParam();
  const { data, error, isFetching } = useGetDomainsCountQuery(mapToApiPayload(tableParams, page), {
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (data) {
      setDomains(data.data);
    } else if (error) {
      message.error('Failed to fetch domains count');
    }
  }, [data, error]);

  // react-router's setSearchParams closes over the search params of its render, so a delayed call needs the latest one.
  const setPageRef = useRef(setPage);
  setPageRef.current = setPage;

  // One transition, so the new params and page 1 render together: the router applies the page in a transition.
  const resetToFirstPage = (updateParams) =>
    startTransition(() => {
      setTableParams(updateParams);
      setPageRef.current(1, { replace: true });
    });

  const handleTableChange = (pagination, filters, sorter) => resetToFirstPage((params) => ({ ...params, sorter }));

  const debouncedSearch = useMemo(
    () => debounce((newInput) => resetToFirstPage((params) => ({ ...params, filters: { domain: [newInput] } })), 1500),
    []
  );
  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const onChangeInput = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    debouncedSearch(newInput);
  };

  const canGoBack = !isFetching && page > 1;
  // The response carries no page count: a full page is treated as having a next one.
  const canGoForward = !isFetching && domains.length === PAGE_SIZE;

  return (
    <AdminTemplate metaTags={META_TAGS}>
      <Content>
        <Flex gap={16} vertical={true} style={{ marginBottom: '16px' }}>
          <Title level={3}>Page count per domain</Title>
          <Card>
            <Input
              placeholder="Please enter domain name"
              allowClear
              value={search}
              onChange={onChangeInput}
              onPressEnter={onChangeInput}
              prefix={<SearchOutlined style={{ color: '#d9d9d9' }} />}
              style={{ marginBottom: 16, width: '60%' }}
            />
            <Table loading={isFetching} pagination={false} dataSource={domains} onChange={handleTableChange} bordered>
              <Table.Column title="Domain" dataIndex="domain" key="domain" width={'60%'} />
              <Table.Column title="Page Count" dataIndex="number_of_urls" key="number_of_urls" sorter={true} />
            </Table>
            {(domains.length > 0 || page > 1) && (
              <PrevNextPagination
                page={page}
                pageSize={PAGE_SIZE}
                currentPageAmount={isFetching ? 0 : domains.length}
                onPrevClick={canGoBack ? () => setPage(page - 1) : undefined}
                onNextClick={canGoForward ? () => setPage(page + 1) : undefined}
              />
            )}
          </Card>
        </Flex>
      </Content>
    </AdminTemplate>
  );
};

export default requireAuth(DomainsCountPage);
